import { Link } from '@tanstack/react-router';
import type { ActivitySummary } from '@painpal/contracts';
import { ChevronRight } from 'lucide-react';
import type { SportFilter } from '../../api/keys';
import { useActivities } from '../../api/queries';
import { LoadCell } from '../../components/LoadCell';
import { LoadMethodBadge } from '../../components/LoadMethodBadge';
import { SportTag } from '../../components/SportTag';
import { EmptyState, ErrorPanel, LoadingBlock } from '../../components/StateViews';
import { formatHoursMinutes, formatMonthDay } from '../../lib/format';
import { sportNoun } from '../../lib/sports';
import { metaLine, sportSearch } from '../activities/activitiesModel';
import styles from './fitness.module.css';

function ActivitiesTable({ items }: { items: ActivitySummary[] }) {
  return (
    <table className={`data-table ${styles.table}`}>
      <thead className="sr-only-phone">
        <tr><th scope="col">DATE</th><th scope="col">SPORT</th><th scope="col">NAME</th><th scope="col">TIME</th><th scope="col">LOAD</th><th scope="col">HOW</th></tr>
      </thead>
      <tbody>
        {items.map((a) => (
          <tr key={a.id}>
            <td data-cell="date">{formatMonthDay(a.localDate)}</td>
            <td data-cell="sport"><SportTag sport={a.sport} /></td>
            <td data-cell="ride">
              <Link to="/activities/$activityId" params={{ activityId: a.id }} className={styles.rideName}>{a.name.toUpperCase()}</Link>
              <span className={styles.rideMeta}>{`${formatMonthDay(a.localDate)} · ${metaLine(a)}`}</span>
            </td>
            <td data-cell="time">{formatHoursMinutes(a.durationS)}</td>
            <td data-cell="load"><LoadCell activity={a} pxPerLoad={0.24} maxBarPx={48} showReason={false} /></td>
            <td data-cell="how"><LoadMethodBadge activity={a} /></td>
            <td data-cell="chevron" aria-hidden="true"><ChevronRight size={14} /></td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Body({ query, sport }: { query: ReturnType<typeof useActivities>; sport: SportFilter }) {
  if (query.isPending) return <LoadingBlock label="LOADING ACTIVITIES" />;
  if (query.isError) return <ErrorPanel error={query.error} onRetry={() => void query.refetch()} />;
  const items = query.data.pages.flatMap((page) => page.items).slice(0, 5);
  if (items.length === 0) {
    return (
      <EmptyState title={`NO RECENT ${sportNoun(sport, 2)}`} body="Upload a .FIT file or connect Strava and your activities land here.">
        <Link to="/activities" search={{ upload: true }} className="btn btn-ink">+ ADD ACTIVITY</Link>
      </EmptyState>
    );
  }
  return <ActivitiesTable items={items} />;
}

export function LatestRides({ sport = 'all' }: { sport?: SportFilter }) {
  const query = useActivities(sport);
  return (
    <section className={styles.latest} aria-labelledby="latest-title">
      <header className="dark-head">
        <h2 id="latest-title" className="dark-title">{`LATEST ${sportNoun(sport, 2)}`}</h2>
        <Link to="/activities" search={sportSearch(sport)} className={styles.darkLink}>{`ALL ${sportNoun(sport, 2)} ↗`}</Link>
      </header>
      <Body query={query} sport={sport} />
    </section>
  );
}
